<template>
  <div v-if="isVisible" class="quick-autoload-expenses-modal-root">
    <div class="modal-backdrop fade show" @click="close"></div>
    <div class="modal fade show" tabindex="-1" @click.self="close">
      <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-magic me-2 text-secondary" aria-hidden="true"></i>
              {{ $t("financialSummary.stages.shipPaymentsAutoloadExpensesTitle") }}
            </h5>
            <button type="button" class="btn-close" aria-label="Close" @click="close"></button>
          </div>
          <div class="modal-body">
            <p class="text-muted small mb-3">
              {{ $t("financialSummary.stages.shipPaymentsAutoloadExpensesHint") }}
            </p>
            <div v-if="shippingName" class="small fw-semibold text-body mb-2">
              {{ shippingName }}
            </div>
            <ContentSkeleton v-if="loading" variant="table" :rows="4" :cols="5" padded />
            <div v-else-if="loadError" class="alert alert-danger mb-0">
              {{ loadError }}
            </div>
            <template v-else>
              <div v-if="expenseTypeSelectOptions.length" class="d-flex justify-content-end mb-2">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary"
                  :disabled="loading || saving"
                  @click="addManualExpenseRow"
                >
                  <i class="fas fa-plus me-1" aria-hidden="true"></i>
                  {{ $t("financialSummary.stages.shipPaymentsAutoloadExpensesAddLine") }}
                </button>
              </div>
              <div v-if="!editableRows.length" class="alert alert-light mb-0">
                {{ $t("financialSummary.stages.shipPaymentsAutoloadExpensesEmpty") }}
              </div>
              <div v-else class="table-responsive">
              <table class="table table-sm table-bordered align-middle mb-0">
                <thead class="table-light">
                  <tr>
                    <th scope="col">{{ $t("financialSummary.stages.shipPaymentsAutoloadExpensesType") }}</th>
                    <th scope="col" class="text-end" style="width: 10rem">
                      {{ $t("financialSummary.stages.shipPaymentsAutoloadExpensesAmount") }}
                    </th>
                    <th scope="col" style="min-width: 16rem">
                      {{ $t("financialSummary.stages.shipPaymentsAutoloadExpensesContact") }}
                    </th>
                    <th scope="col" style="min-width: 12rem">
                      {{ $t("File") }}
                    </th>
                    <th scope="col" class="text-center" style="width: 4rem">
                      <span class="visually-hidden">{{
                        $t("financialSummary.stages.shipPaymentsAutoloadExpensesRemove")
                      }}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in editableRows" :key="row.key">
                    <td class="small">
                      <template v-if="row.manualAdded">
                        <select
                          v-model="row.typeIdDraft"
                          class="form-select form-select-sm"
                          :disabled="loading || saving"
                        >
                          <option value="">
                            {{ $t("financialSummary.stages.shipPaymentsAutoloadExpensesPickType") }}
                          </option>
                          <option
                            v-for="opt in expenseTypeSelectOptions"
                            :key="'exp-type-' + row.key + '-' + opt.id"
                            :value="opt.id"
                          >
                            {{ opt.label }}
                          </option>
                        </select>
                      </template>
                      <template v-else>
                        <span
                          v-if="!row.existingUpdate"
                          class="badge bg-secondary-subtle text-secondary-emphasis me-1"
                        >{{
                          $t("financialSummary.stages.shipPaymentsAutoloadExpensesNewBadge")
                        }}</span>
                        {{ row.typeLabel }}
                      </template>
                    </td>
                    <td class="text-end">
                      <input
                        v-model="row.amountDraft"
                        type="text"
                        inputmode="decimal"
                        class="form-control form-control-sm text-end"
                        autocomplete="off"
                      />
                    </td>
                    <td class="small align-middle quick-autoload-exp-contact-cell">
                      <FieldReference
                        :field="expenseContactFieldDef"
                        :value="row.contactRefValue"
                        mode="edit"
                        :field-id="'autoload-exp-contact-' + row.key"
                        editing-fields="contact"
                        :show-label="false"
                        :popup="true"
                        :reference-options="contactReferenceOptions"
                        disable-lock
                        @input="(v) => onExpenseContactInput(row, v)"
                        @update:editing-fields="() => {}"
                        @open-reference-modal="() => {}"
                      />
                    </td>
                    <td class="small align-middle quick-autoload-exp-file-cell">
                      <FieldFile
                        :field="expenseFileFieldDef"
                        :value="row.fileRefValue"
                        mode="edit"
                        :field-id="'autoload-exp-file-' + row.key"
                        editing-fields="file"
                        :show-label="false"
                        disable-lock
                        @input="(v) => onExpenseFileInput(row, v)"
                        @update:editing-fields="() => {}"
                      />
                    </td>
                    <td class="text-center">
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-danger"
                        :title="$t('financialSummary.stages.shipPaymentsAutoloadExpensesRemove')"
                        @click="removeRow(row)"
                      >
                        <i class="fas fa-times" aria-hidden="true"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
              </div>
            </template>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="close">
              {{ $t("cancel") }}
            </button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="loading || saving || !!loadError || !editableRows.length"
              @click="submit"
            >
              <span
                v-if="saving"
                class="spinner-border spinner-border-sm me-1"
                role="status"
                aria-hidden="true"
              ></span>
              {{ $t("financialSummary.stages.shipPaymentsAutoloadExpensesSave") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import Swal from "sweetalert2";
import { getTranslatedListLabel } from "@/utils/listItemI18n";
import { FieldReference, FieldFile } from "../model-definition-core";
import {
  fileRefValueFromExpenseDoc,
  serializeExpenseFileForApi,
  fileFieldDefFromShippingDefinition,
} from "@/utils/shippingExpenseFileField";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";
import "vue-multiselect/dist/vue-multiselect.min.css";

function expenseRefToId(expense) {
  if (expense == null || expense === "") return null;
  if (typeof expense === "object" && expense._id != null) return String(expense._id);
  const s = String(expense).trim();
  if (/^[a-f0-9]{24}$/i.test(s)) return s;
  return null;
}

function typeRefToId(type) {
  if (type == null || type === "") return null;
  if (typeof type === "object" && type._id != null) return String(type._id);
  const s = String(type).trim();
  if (/^[a-f0-9]{24}$/i.test(s)) return s;
  return null;
}

/** Valor del FieldReference (contact) → id para API. */
function contactRefValueToId(val) {
  if (val == null || val === "") return "";
  if (typeof val === "object" && val._id != null) return String(val._id);
  const s = String(val).trim();
  if (/^[a-f0-9]{24}$/i.test(s)) return s;
  return "";
}

/** Inicializa `contact` del stub para el mismo contrato que Form2 / FieldReference. */
function stubToContactRefValue(stub) {
  const c = stub && stub.contact;
  if (c == null || c === "") return null;
  if (typeof c === "object" && c._id != null) {
    const name = (c.name && String(c.name).trim()) || String(c._id);
    return { _id: c._id, name };
  }
  const s = String(c).trim();
  if (/^[a-f0-9]{24}$/i.test(s)) return s;
  return null;
}

/** Valor inicial para FieldFile según isArray del ModelDefinition. */
function stubToFileRefValue(stub, isArray = false) {
  return fileRefValueFromExpenseDoc(stub && stub.file, isArray);
}

function parseAmountDraft(raw) {
  if (raw === "" || raw === null || raw === undefined) return null;
  const n = parseFloat(String(raw).replace(",", ".").trim());
  return Number.isFinite(n) ? n : NaN;
}

function deepCloneJson(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function stripPopulatedRefsForApi(obj) {
  if (!obj || typeof obj !== "object") return obj;
  const out = { ...obj };
  for (const k of Object.keys(out)) {
    const v = out[k];
    if (v && typeof v === "object" && !Array.isArray(v) && !(v instanceof Date) && v._id != null) {
      out[k] = String(v._id);
    }
  }
  return out;
}

function normalizeExistingExpenseForPut(exp) {
  const id = expenseRefToId(exp);
  if (!id) return null;
  const src = typeof exp === "object" && exp !== null ? { ...exp } : { _id: id };
  const out = stripPopulatedRefsForApi(src);
  out._id = id;
  return out;
}

export default {
  name: "QuickAutoloadExpensesModal",
  components: { ContentSkeleton, FieldReference, FieldFile },
  props: {
    isVisible: { type: Boolean, default: false },
    shippingId: { type: String, default: "" },
    shippingName: { type: String, default: "" },
    lists: { type: Array, default: () => [] },
  },
  data() {
    return {
      loading: false,
      saving: false,
      loadError: "",
      baseShippingDoc: null,
      editableRows: [],
      /** Valores de la lista del campo `type` en shipping → expenses (misma fuente que ViewFieldList). */
      expenseTypeListValues: [],
      /** Opciones para `FieldReference` preload (una carga para todas las filas). */
      contactReferenceOptions: [],
      expenseFileFieldDefLoaded: fileFieldDefFromShippingDefinition([]),
    };
  },
  computed: {
    /** Misma forma que el subcampo `contact` del grupo expenses en shipping (reference → contact, preload). */
    expenseContactFieldDef() {
      return {
        type: "reference",
        ref: "contact",
        key: "contact",
        label: "Contact",
        preload: true,
        isArray: false,
        readonly: false,
        safe: false,
        hide: false,
      };
    },
    expenseFileFieldDef() {
      return this.expenseFileFieldDefLoaded;
    },
    /** Opciones del select de tipo (misma lista que el campo `type` en shipping → expenses). */
    expenseTypeSelectOptions() {
      const vals = this.expenseTypeListValues;
      if (!Array.isArray(vals) || !vals.length) return [];
      const loc = (this.$i18n && this.$i18n.locale) || "es";
      const out = [];
      const seen = new Set();
      for (const opt of vals) {
        if (!opt) continue;
        const id = opt._id != null ? String(opt._id) : null;
        if (!id || seen.has(id)) continue;
        seen.add(id);
        const chain = this.buildHierarchyChain(vals, id);
        let label;
        if (chain.length > 1) {
          label = chain.map((it) => getTranslatedListLabel(it, loc)).filter(Boolean).join(" > ");
        } else {
          label = getTranslatedListLabel(chain[0] || opt, loc);
        }
        out.push({ id, label: label || id });
      }
      return out;
    },
  },
  watch: {
    isVisible(val) {
      if (val && this.shippingId) {
        this.loadDraft();
      }
      if (!val) {
        this.resetLocal();
      }
    },
    shippingId() {
      if (this.isVisible && this.shippingId) {
        this.loadDraft();
      }
    },
  },
  methods: {
    resetLocal() {
      this.loadError = "";
      this.baseShippingDoc = null;
      this.editableRows = [];
      this.expenseTypeListValues = [];
      this.contactReferenceOptions = [];
    },
    async loadContactReferenceOptions() {
      this.contactReferenceOptions = [];
      try {
        const params = new URLSearchParams();
        params.append("perPage", "1000");
        params.append("fields", "_id,name,label,textPlainVersion");
        const { data } = await wencesApi.get(`/api/gotrading/dynamic/contact?${params.toString()}`);
        const items = (data && (data.items || data)) || [];
        const valid = Array.isArray(items)
          ? items.filter((x) => x && (x._id || x.name))
          : [];
        this.contactReferenceOptions = valid;
      } catch (e) {
        console.warn("QuickAutoloadExpensesModal: load contacts", e);
        this.contactReferenceOptions = [];
      }
    },
    onExpenseContactInput(row, val) {
      this.$set(row, "contactRefValue", val);
    },
    onExpenseFileInput(row, val) {
      this.$set(row, "fileRefValue", val);
    },
    findOptionInListValues(values, valueId) {
      if (valueId == null || valueId === "" || !Array.isArray(values)) return null;
      const sid = String(valueId);
      return (
        values.find((opt) => {
          if (!opt) return false;
          const optId = opt._id != null ? opt._id : opt.id;
          return optId != null && String(optId) === sid;
        }) || null
      );
    },
    buildHierarchyChain(values, itemId, visited = new Set()) {
      if (!itemId || visited.has(String(itemId))) return [];
      visited.add(String(itemId));
      const item = this.findOptionInListValues(values, itemId);
      if (!item) return [];
      if (item.parent) {
        let parentId;
        if (typeof item.parent === "object" && item.parent != null && item.parent.toString) {
          parentId = item.parent.toString();
        } else {
          parentId = String(item.parent);
        }
        return [...this.buildHierarchyChain(values, parentId, visited), item];
      }
      return [item];
    },
    /**
     * Misma idea que ViewFieldList / ViewContent.getListName: id de lista → label (i18n en `labels`).
     */
    labelForExpenseTypeValue(stub) {
      const loc = (this.$i18n && this.$i18n.locale) || "es";
      if (stub && typeof stub.type === "object" && stub.type !== null) {
        if (
          (stub.type.label != null && stub.type.label !== "") ||
          (stub.type.labels && typeof stub.type.labels === "object" && !Array.isArray(stub.type.labels))
        ) {
          return getTranslatedListLabel(stub.type, loc) || "—";
        }
      }
      const tid = typeRefToId(stub && stub.type);
      if (!tid) return "—";
      const vals = this.expenseTypeListValues;
      if (!Array.isArray(vals) || vals.length === 0) return tid;
      const chain = this.buildHierarchyChain(vals, tid);
      if (chain.length > 1) {
        return chain.map((it) => getTranslatedListLabel(it, loc)).filter(Boolean).join(" > ") || tid;
      }
      const opt = chain.length === 1 ? chain[0] : this.findOptionInListValues(vals, tid);
      if (opt) {
        const text = getTranslatedListLabel(opt, loc);
        return text || tid;
      }
      return tid;
    },
    async ensureExpenseTypeListValues() {
      this.expenseTypeListValues = [];
      let listType = null;
      try {
        const { data: def } = await wencesApi.get("/api/gotrading/model-definitions/shipping");
        const fields = Array.isArray(def && def.fields) ? def.fields : [];
        this.expenseFileFieldDefLoaded = fileFieldDefFromShippingDefinition(fields);
        const expensesG = fields.find((f) => f && f.key === "expenses" && f.type === "group");
        const inner = Array.isArray(expensesG && expensesG.fields) ? expensesG.fields : [];
        const typeF = inner.find((f) => f && f.key === "type" && f.type === "list");
        if (typeF && typeF.listType) listType = String(typeF.listType);
      } catch (e) {
        console.warn("QuickAutoloadExpensesModal: shipping model definition", e);
      }
      if (!listType) return;
      const fromProps = (this.lists || []).find((l) => l && l.type === listType && Array.isArray(l.values));
      if (fromProps && fromProps.values.length) {
        this.expenseTypeListValues = fromProps.values;
        return;
      }
      try {
        const { data: listDoc } = await wencesApi.get(
          `/api/gotrading/lists/type/${encodeURIComponent(listType)}`
        );
        if (listDoc && Array.isArray(listDoc.values)) {
          this.expenseTypeListValues = listDoc.values;
        }
      } catch (e) {
        console.warn("QuickAutoloadExpensesModal: list type", listType, e);
      }
    },
    close() {
      this.$emit("close");
    },
    removeRow(row) {
      this.editableRows = this.editableRows.filter((r) => r.key !== row.key);
    },
    addManualExpenseRow() {
      const key = `manual-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      this.editableRows.push({
        key,
        stub: {},
        manualAdded: true,
        typeIdDraft: "",
        typeLabel: "",
        amountDraft: "",
        contactRefValue: null,
        fileRefValue: null,
      });
    },
    async loadDraft() {
      this.loading = true;
      this.loadError = "";
      this.baseShippingDoc = null;
      this.editableRows = [];
      try {
        const { data: base } = await wencesApi.get(`/api/gotrading/dynamic/shipping/${this.shippingId}`);
        if (!base || !base._id) {
          this.loadError = this.$t("financialSummary.stages.shipPaymentsAutoloadExpensesLoadError");
          return;
        }
        this.baseShippingDoc = base;
        await Promise.all([this.ensureExpenseTypeListValues(), this.loadContactReferenceOptions()]);
        const item = deepCloneJson(base);
        const { data: preloadWrap } = await wencesApi.post(`/api/gotrading/dynamic/shipping/preload`, {
          action: "button_expenses",
          item,
        });
        const merged = preloadWrap && preloadWrap.data != null ? preloadWrap.data : preloadWrap;
        if (!merged || typeof merged !== "object") {
          this.loadError = this.$t("financialSummary.stages.shipPaymentsAutoloadExpensesLoadError");
          return;
        }
        const expArr = Array.isArray(merged.expenses) ? merged.expenses : [];
        const proposals = expArr.filter((e) => {
          if (!e || typeof e !== "object") return false;
          const id = expenseRefToId(e);
          if (e._autoloadInvoiceSupplierSync === true && id) return true;
          if (e._autoloadFreight2Sync === true && id) return true;
          if (id) return false;
          return true;
        });
        const rows = proposals.map((stub, idx) => {
          const amount = stub.amount;
          const draft = amount === null || amount === undefined || amount === "" ? "" : String(Number(amount));
          const existingUpdate =
            (stub._autoloadInvoiceSupplierSync === true || stub._autoloadFreight2Sync === true) &&
            !!expenseRefToId(stub);
          return {
            key: `${existingUpdate ? "upd" : "new"}-${idx}-${Math.random().toString(36).slice(2, 9)}`,
            stub: { ...stub },
            manualAdded: false,
            existingUpdate,
            typeIdDraft: "",
            typeLabel: this.labelForExpenseTypeValue(stub),
            amountDraft: draft,
            contactRefValue: stubToContactRefValue(stub),
            fileRefValue: stubToFileRefValue(stub, this.expenseFileFieldDef.isArray),
          };
        });
        this.editableRows = rows;
      } catch (e) {
        console.error("QuickAutoloadExpensesModal loadDraft:", e);
        const msg =
          (e.response && e.response.data && (e.response.data.message || e.response.data.error)) ||
          e.message ||
          "";
        this.loadError = msg || this.$t("financialSummary.stages.shipPaymentsAutoloadExpensesLoadError");
      } finally {
        this.loading = false;
      }
    },
    rowToExpenseStub(row, parsed) {
      const stub = stripPopulatedRefsForApi({ ...row.stub });
      if (!row.existingUpdate) {
        delete stub._id;
      }
      delete stub._autoloadInvoiceSupplierSync;
      delete stub._autoloadFreight2Sync;
      if (row.manualAdded) {
        stub.type = String(row.typeIdDraft).trim();
      }
      stub.amount = parsed;
      if (row.existingUpdate) {
        stub.remaining = parsed;
      }
      const contactId = contactRefValueToId(row.contactRefValue);
      if (contactId) {
        stub.contact = contactId;
      } else {
        delete stub.contact;
      }
      const fv = row.fileRefValue;
      const serialized = serializeExpenseFileForApi(
        fv,
        this.expenseFileFieldDef.isArray
      );
      if (serialized != null) {
        stub.file = serialized;
      } else {
        delete stub.file;
      }
      return stub;
    },
    buildExpensesPayload() {
      const base = this.baseShippingDoc;
      if (!base) return null;
      const existing = (Array.isArray(base.expenses) ? base.expenses : [])
        .map((e) => normalizeExistingExpenseForPut(e))
        .filter(Boolean);
      const newPayloads = [];
      const updatesById = new Map();
      for (const row of this.editableRows) {
        const draft = row.amountDraft;
        const parsed = parseAmountDraft(draft);
        if (draft === "" || draft === null || draft === undefined) {
          continue;
        }
        if (Number.isNaN(parsed)) {
          throw new Error(this.$t("financialSummary.stages.shipPaymentsAutoloadExpensesInvalidNumber"));
        }
        if (
          row.manualAdded &&
          (row.typeIdDraft == null || String(row.typeIdDraft).trim() === "")
        ) {
          throw new Error(this.$t("financialSummary.stages.shipPaymentsAutoloadExpensesMissingType"));
        }
        const stub = this.rowToExpenseStub(row, parsed);
        if (row.existingUpdate && stub._id) {
          updatesById.set(String(stub._id), stub);
        } else {
          newPayloads.push(stub);
        }
      }
      const mergedExisting = existing.map((exp) => {
        const patch = updatesById.get(String(exp._id));
        return patch ? { ...exp, ...patch } : exp;
      });
      return [...mergedExisting, ...newPayloads];
    },
    async submit() {
      if (!this.shippingId || !this.baseShippingDoc) return;
      let expensesPayload;
      try {
        expensesPayload = this.buildExpensesPayload();
      } catch (err) {
        await Swal.fire({
          icon: "warning",
          title: this.$tn("financialSummary.stages.shipPaymentsAutoloadExpensesInvalidTitle"),
          text: err.message || this.$tn("error"),
        });
        return;
      }
      this.saving = true;
      try {
        await wencesApi.put(`/api/gotrading/dynamic/shipping/${this.shippingId}`, {
          partialUpdate: true,
          saveTrigger: "manual",
          expenses: expensesPayload,
        });
        await Swal.fire({
          icon: "success",
          title: this.$tn("financialSummary.stages.shipPaymentsAutoloadExpensesSaved"),
          timer: 1800,
          showConfirmButton: false,
        });
        this.$emit("saved");
        this.close();
      } catch (e) {
        console.error("QuickAutoloadExpensesModal submit:", e);
        const msg =
          (e.response && e.response.data && (e.response.data.message || e.response.data.error)) ||
          e.message ||
          "";
        await Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: msg || this.$tn("financialSummary.stages.shipPaymentsAutoloadExpensesSaveError"),
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.quick-autoload-expenses-modal-root {
  position: fixed;
  inset: 0;
  z-index: 1055;
}
.quick-autoload-expenses-modal-root .modal-backdrop {
  z-index: 1;
}
.quick-autoload-expenses-modal-root .modal {
  display: block;
  position: relative;
  z-index: 2;
}

.quick-autoload-exp-contact-cell {
  vertical-align: middle;
}
.quick-autoload-exp-file-cell {
  vertical-align: middle;
  max-width: 14rem;
}

/* Mismo control que Form2: ocultar lupa del modal de búsqueda extra (solo multiselect). */
.quick-autoload-expenses-modal-root >>> .reference-search-btn {
  display: none !important;
}
</style>
